import { computed, watch, type MaybeRefOrGetter, toValue } from "vue";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type { AdminBannedIpsListQueryParams, CreateBannedIpRequest } from "@shopexperts/legacy-api";
import { BAN_LIST_QUERY_KEY } from "./useBanRules";

export const BANNED_IPS_QUERY_KEY = [...BAN_LIST_QUERY_KEY, "ips"] as const;

type BannedIpsQuery = Omit<AdminBannedIpsListQueryParams, "cursor">;

export function useBannedIps(query: MaybeRefOrGetter<BannedIpsQuery | undefined> = {}) {
    const api = useApi();
    const toaster = useToaster();

    const queryResult = useInfiniteQuery({
        queryKey: computed(() => [...BANNED_IPS_QUERY_KEY, "list", toValue(query) ?? {}]),
        queryFn: ({ pageParam }) => api.admin.banList.ips.list({
            query: toValue(query),
            cursor: pageParam,
        }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.pagination.has_next
            ? lastPage.pagination.cursor ?? undefined
            : undefined,
        staleTime: 60_000,
        gcTime: 5 * 60_000,
    });

    watch(queryResult.error, (error) => {
        if (error) {
            toaster.create({ title: "Failed to load banned IPs", type: "error" });
        }
    });

    return queryResult;
}

export function useBannedIpMutations() {
    const api = useApi();
    const queryClient = useQueryClient();
    const toaster = useToaster();

    const invalidateBanList = () => {
        queryClient.invalidateQueries({ queryKey: [...BAN_LIST_QUERY_KEY] });
    };

    const recordIp = useMutation({
        mutationFn: (body: CreateBannedIpRequest) => api.admin.banList.ips.create({ body }),
        onSuccess: () => {
            toaster.create({ title: "IP address added to the registry", type: "success" });
            invalidateBanList();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to add the IP address: ${error.message}`, type: "error" });
        },
    });

    const removeIp = useMutation({
        mutationFn: (bannedIpId: number | string) => (
            api.admin.banList.ips.delete({ params: { bannedIpId } })
        ),
        onSuccess: () => {
            toaster.create({ title: "IP address removed from the registry", type: "success" });
            invalidateBanList();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to remove the IP address: ${error.message}`, type: "error" });
        },
    });

    return {
        recordIp,
        removeIp,
    };
}
